import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { formatearFecha, obtenerFechaDeNegocioHoy } from '../../../nucleo/fecha/fechaLocal.js';
import { obtenerPlanPorMovimiento } from '../api/msiApi.js';
import {
  importe as formatearImporte, LEYENDA_REPARTO_DE_DEUDA,
  NOMBRE_DE_ESTADO_DE_MENSUALIDAD, NOMBRE_DE_ESTADO_DE_PLAN,
} from '../formato.js';

/**
 * BLOQUE DEL PLAN EN EL DETALLE DEL GASTO (RF-077.3, RF-074).
 *
 * DOS ESTADOS Y LOS DOS SON LEGÍTIMOS:
 *   · El gasto YA TIENE PLAN → se muestran sus mensualidades con su contador y su
 *     estado presentado.
 *   · El gasto está MARCADO A MESES PERO NO TIENE PLAN TODAVÍA → se muestra
 *     "Pendiente de plan" con la acción "Generar plan". ES UN ESTADO VÁLIDO Y
 *     ESPERADO, no un error (RF-074, RN-098d): el 3/6 lleva capturándolo así desde
 *     su cierre y el plan se puede generar meses después. NO AFECTA NINGÚN CÁLCULO:
 *     su gasto ya está completo en la deuda y en los totales de su mes.
 *
 * EL DETALLE DEL GASTO NO SE REPARTE NUNCA (RN-120e): arriba, en la pantalla del
 * 3/6, el importe son siempre los de la compra. Este bloque añade el desglose, no
 * lo sustituye.
 */
export function BloqueDePlanDelGasto({ movimiento }) {
  const hoy = obtenerFechaDeNegocioHoy();

  const [plan, setPlan] = useState(null);
  const [sinPlan, setSinPlan] = useState(false);

  useEffect(() => {
    if (!movimiento?.esMesesSinIntereses) return;

    (async () => {
      try {
        setPlan(await obtenerPlanPorMovimiento(movimiento.idMovimiento, hoy));
        setSinPlan(false);
      } catch (fallo) {
        // PLAN_INEXISTENTE no es un fallo: es "todavía no tiene plan" (RF-074).
        if (fallo.codigo === 'PLAN_INEXISTENTE') setSinPlan(true);
      }
    })();
  }, [movimiento, hoy]);

  if (!movimiento?.esMesesSinIntereses) return null;

  if (sinPlan) {
    return (
      <div className="card" style={{ margin: 12 }} data-prueba="pendiente-de-plan">
        <h4 style={{ margin: '0 0 6px', fontSize: 11.5 }}>MESES SIN INTERESES</h4>
        <div className="fila">
          <span className="crece"><b>Pendiente de plan</b></span>
          <span className="chip">SIN PLAN</span>
        </div>
        <span className="ayuda">
          Marcaste esta compra a meses pero <b>todavía no tiene plan</b>. No es un error y no afecta
          ningún cálculo: el gasto ya está completo en la deuda de la tarjeta. Puedes generarlo
          cuando quieras, incluso meses después.
        </span>
        {movimiento.esPropio && (
          <Link className="btn ancho" to={`/cuentas/meses/generar/${movimiento.idMovimiento}`}>
            Generar plan
          </Link>
        )}
      </div>
    );
  }

  if (!plan) return null;

  return (
    <div className="card" style={{ margin: 12 }} data-prueba="bloque-de-plan">
      <h4 style={{ margin: '0 0 6px', fontSize: 11.5 }}>PLAN DE MESES SIN INTERESES</h4>

      <div className="fila">
        <span className="crece">Plan</span>
        <b>{plan.numeroDeMeses} meses</b>
      </div>
      <div className="fila">
        <span className="crece">Estado</span>
        <b>{NOMBRE_DE_ESTADO_DE_PLAN[plan.claveEstado] ?? plan.nombreEstado}</b>
      </div>
      <div className="fila">
        <span className="crece">Falta por caer</span>
        <b>{formatearImporte(plan.saldoDelPlan, plan.codigoISO)}</b>
      </div>
      <span className="ayuda">{LEYENDA_REPARTO_DE_DEUDA}</span>

      <div className="lista">
        {plan.mensualidades.map((mensualidad) => (
          <div className="fila" key={mensualidad.idMensualidad}>
            <div className="crece">
              <b data-prueba="contador">{mensualidad.contadorDeMensualidad}</b>
              <span className="chip">{NOMBRE_DE_ESTADO_DE_MENSUALIDAD[mensualidad.estadoPresentado]}</span>
              <small>corte {formatearFecha(mensualidad.fechaCorte)}</small>
            </div>
            <span className="imp">{formatearImporte(mensualidad.importe, plan.codigoISO)}</span>
          </div>
        ))}
      </div>

      <Link className="btn sec ancho" to={`/cuentas/meses/planes/${plan.idPlan}`}>
        Abrir el panel del plan
      </Link>
    </div>
  );
}
